/**
 * Pure decision logic for AIChatWidget's ESG-3952 large-scope routing.
 *
 * When a chat request against a report doc is large-scope (new section(s), whole-doc
 * rewrite, "regenerate"), the backend (POST /v0/chat, see esgai/app.py) routes it to the
 * report-writer task pipeline instead of running it synchronously, and returns
 * `{ queued: true, task_id, reportdoc_id }` instead of a normal `{ thread: {...} }` chat
 * response. Extracted here (rather than left inline in AIChatWidget.jsx) so this decision -
 * and the race it exists to prevent - can be unit tested without the widget's full React
 * Query / Redux / Auth0 provider tree, matching this codebase's convention of pulling pure
 * logic out of components into standalone testable modules (see ISSB-helpers.js).
 */

/**
 * True if a postChat() response indicates the request was queued as a report-writer task
 * (large-scope canvas update) rather than answered synchronously.
 * @param {object|null|undefined} response
 * @returns {boolean}
 */
export function isQueuedTaskResponse(response) {
  return !!response?.queued;
}

/**
 * Decide what AIChatWidget's pollThread() should do on its next tick, given the current
 * polling-state flags. Encapsulates the ESG-3952 race guard: while we're still waiting to
 * learn whether a fire-and-forget postChat() call queued a report-writer task (which never
 * touches the CURRENT threadId at all), pollThread() must not act on that threadId's data -
 * it could be stale from a PRIOR exchange (e.g. still showing state:"done" from before this
 * message was sent), which would make pollThread() fire the wrong response.
 *
 * @param {{ awaitingQueueCheck: boolean, polling: boolean }} pollingState
 * @returns {"wait_for_queue_check" | "poll_thread" | "stop"}
 */
export function decidePollThreadAction(pollingState) {
  if (pollingState?.awaitingQueueCheck) return "wait_for_queue_check";
  if (pollingState?.polling) return "poll_thread";
  return "stop";
}
